export type StepKey =
    | "details"
    | "event-days"
    | "images"
    | "seatmap"
    | "tickets"
    | "data-collection"
    | "coupons"
    | "promoters"
    | "refund-policy"
    | "mailing"
    | "reminders"
    | "sponsors"
    | "speakers"
    | "feedback"
    | "options"
    | "review";

export type FieldHelp = {
    field: string;
    description: string;
    tips?: string[];
};

export type StepHelp = {
    overview: string;
    fields: FieldHelp[];
};

export const STEP_HELP: Record<StepKey, StepHelp> = {
    seatmap: {
        overview:
            "Optionally assign a seat map so attendees can pick their exact seats at checkout. Seat maps are managed centrally; pick one from the library and assign it to this event.",
        fields: [
            {
                field: "Assigning a seat map",
                description:
                    "Choose a seat map from the available library to enable reserved seating for this event. You can swap it for another at any time, or remove it to sell general-admission tickets only.",
                tips: [
                    "Assign the seat map before configuring tickets so you can map ticket categories to seat categories.",
                    "If the seat map you need isn't listed, ask an administrator to add it to the library.",
                ],
            },
        ],
    },
    details: {
        overview:
            "Set the core information for your event. These details are shown publicly on the event listing page and help attendees decide whether to register.",
        fields: [
            {
                field: "Event Type",
                description:
                    "Controls who can see and register for your event. Public events appear in search results and on your profile. Private events require a direct link to access. Invite Only events require you to manually approve each attendee.",
                tips: [
                    "Use Public for most events to maximise reach.",
                    "Use Invite Only for corporate or exclusive gatherings where you want to control attendance.",
                ],
            },
            {
                field: "Event Name",
                description:
                    "The title that appears on your event listing, tickets, and confirmation emails. Make it specific and descriptive so people know exactly what to expect.",
                tips: [
                    "Avoid vague names like 'Event 2024' — include the key topic or experience.",
                    "Keep it under 60 characters so it doesn't get truncated in search results.",
                ],
            },
            {
                field: "Category",
                description:
                    "Categorising your event correctly helps attendees discover it when browsing by interest. Choose the category that best represents the primary focus of your event.",
            },
            {
                field: "Subcategory",
                description:
                    "Narrows down the category for more precise discovery. Only available for categories that have subcategories defined.",
            },
            {
                field: "Tags",
                description:
                    "Keywords that help your event appear in search results. Think about words your target audience would type when looking for this kind of event.",
                tips: [
                    "Add 3–8 relevant tags for best results.",
                    "Include synonyms and related terms (e.g. 'yoga' and 'wellness').",
                ],
            },
            {
                field: "Start & End Date",
                description:
                    "The exact date and time your event begins and ends. Attendees see this prominently on the listing and in their confirmation email.",
                tips: [
                    "Set the end time accurately — it affects calendar invites sent to attendees.",
                    "If your event spans multiple days, set the end date to the last day.",
                ],
            },
            {
                field: "Recurring",
                description:
                    "Enable this if your event repeats on a regular schedule — weekly classes, monthly meetups, annual conferences, etc. You can set the frequency (daily, weekly, monthly) and when the series ends.",
                tips: [
                    "Each occurrence in a recurring series is managed separately after creation.",
                    "Set a recurrence end date to avoid creating unlimited future occurrences.",
                ],
            },
            {
                field: "Description",
                description:
                    "Your event's main pitch to potential attendees. Use markdown to add structure with headings, bold text, bullet points, and links. A good description answers: What will attendees experience? Who is it for? What should they bring or know in advance?",
                tips: [
                    "Lead with the most compelling detail — the first 2 sentences are shown in previews.",
                    "Include the schedule or agenda if relevant.",
                    "Mention any prerequisites, dress code, or age restrictions.",
                ],
            },
            {
                field: "Location Type",
                description:
                    "Whether your event takes place in person, online, or both. This determines which location fields appear below.",
            },
            {
                field: "Venue & Address",
                description:
                    "The physical location of your event. The venue name appears on the listing (e.g. 'Windhoek Country Club') and the address is used to generate a map preview for attendees.",
                tips: [
                    "Include the street address, suburb, and city for accurate map results.",
                    "Add a landmark or parking note in the description if the venue is hard to find.",
                ],
            },
            {
                field: "Virtual Platform",
                description:
                    "The video conferencing tool you'll use for the online session. Attendees will see this in their confirmation email so they can prepare ahead of time.",
            },
            {
                field: "Virtual Link",
                description:
                    "The direct join URL for your online event (Zoom link, Teams meeting link, etc.). This is sent to registered attendees — do not share it publicly if you want to restrict attendance.",
            },
        ],
    },

    "event-days": {
        overview:
            "Define the individual days of your event. You can generate days automatically from the event start and end dates, or add them manually.",
        fields: [
            {
                field: "Day name",
                description: "A label for the day (e.g. Day 1, Saturday, Workshop Day). Shown on tickets and attendee schedules.",
            },
            {
                field: "Start time",
                description: "The date and time this day begins. Used to control ticket validity and check-in windows.",
            },
            {
                field: "End time",
                description: "The date and time this day ends.",
            },
            {
                field: "Generate Days",
                description: "Automatically creates one day entry per calendar day between the event start and end dates.",
                tips: [
                    "Use Generate Days as a starting point, then rename or adjust times as needed.",
                    "Generating replaces any existing days — confirm before proceeding.",
                ],
            },
        ],
    },

    images: {
        overview:
            "Upload images that represent your event visually. A strong listing image and a wide cover banner significantly increase click-through rates on the events page.",
        fields: [
            {
                field: "Listing Image",
                description:
                    "Displayed as a square thumbnail in event search results, category pages, and your business profile. This is the first visual impression most attendees will have of your event.",
                tips: [
                    "Recommended size: 800 × 800 px (1:1 ratio).",
                    "Use a bold, high-contrast image that reads well at small sizes.",
                    "Avoid text-heavy images — they become unreadable as thumbnails.",
                ],
            },
            {
                field: "Cover Image",
                description:
                    "A wide banner shown at the top of the event detail page. This is your primary marketing asset — use it to set the mood and atmosphere of your event.",
                tips: [
                    "Recommended size: 1600 × 600 px (roughly 8:3 ratio).",
                    "Keep the focal point centred — edges may be cropped on mobile.",
                    "Accepted formats: JPG, PNG, WebP. Max file size: 5 MB.",
                ],
            },
            {
                field: "Gallery",
                description:
                    "Optional extra photos shown in a gallery on the event detail page, in the order you set. Each image can carry a short caption.",
                tips: [
                    "Use photos from past editions, the venue or the line-up.",
                    "Captions are optional and limited to 255 characters.",
                    "A gallery holds up to 20 images.",
                ],
            },
        ],
    },

    tickets: {
        overview:
            "Define the ticket types available for your event. You can offer free or paid tickets, set capacity limits, early bird pricing, and control the sale window for each ticket type.",
        fields: [
            {
                field: "Ticket Name",
                description:
                    "The name that appears on the ticket and during checkout. Use names that clearly describe the access level (e.g. 'General Admission', 'VIP', 'Early Bird').",
            },
            {
                field: "Price",
                description:
                    "Set to 0 for free tickets. For paid tickets, enter the price the attendee pays. If your business covers the platform fee, this is the full amount the attendee sees. If fees are passed through, the checkout will show the fee breakdown.",
                tips: [
                    "Use the price calculator shown below the field to check what attendees will pay after fees.",
                ],
            },
            {
                field: "Capacity",
                description:
                    "The maximum number of tickets that can be sold for this ticket type. Leave blank or set to 0 for unlimited capacity (useful for free events).",
            },
            {
                field: "Max Per Order",
                description:
                    "Limits how many of this ticket type a single customer can purchase in one order. Useful for preventing bulk-buying of limited tickets.",
            },
            {
                field: "Sale Window",
                description:
                    "Optionally restrict when this ticket type can be purchased. If blank, sales start immediately when the event is published and end when the event starts.",
                tips: [
                    "Use start dates to release new ticket types on a schedule.",
                    "Use end dates to create urgency (e.g. early bird tickets expiring a week before the event).",
                ],
            },
            {
                field: "Early Bird",
                description:
                    "Marks this ticket type as an early bird offer. This badge is shown to attendees on the listing page to create a sense of urgency.",
            },
            {
                field: "Display",
                description:
                    "Controls whether this ticket type is visible to attendees on the public listing. Hidden ticket types can still be sold via direct link or manual order.",
            },
            {
                field: "Membership Lookup",
                description:
                    "Requires the attendee to enter a valid membership number during checkout for this ticket type. Use this to offer member-only or member-discounted tickets.",
            },
            {
                field: "Collect Custom Fields",
                description:
                    "When enabled, attendees buying this ticket type will be asked to fill in the custom data collection fields you define in the Data Collection step.",
            },
        ],
    },

    "data-collection": {
        overview:
            "Configure what additional information you collect from attendees at checkout. Toggle built-in data sets on or off, and add custom fields specific to your event.",
        fields: [
            {
                field: "Collect Details Per Ticket",
                description:
                    "When enabled, the checkout form asks for attendee details separately for each ticket in an order. Useful for events where each ticket is for a different person (e.g. a family registration).",
                tips: [
                    "Leave this off for most events where one person buys tickets for multiple seats.",
                ],
            },
            {
                field: "Single Ticket Per Order",
                description:
                    "Restricts each order to a single ticket. Useful for events with assigned seating or where one registration = one individual.",
            },
            {
                field: "Membership Lookup",
                description:
                    "Requires attendees to enter a valid membership number to complete checkout. Use this for members-only events or to verify membership eligibility.",
            },
            {
                field: "Medical Aid Details",
                description:
                    "Adds a section at checkout to collect the attendee's medical aid provider and member number. Recommended for sporting events or activities with physical risk.",
            },
            {
                field: "Emergency Contact",
                description:
                    "Adds a section at checkout to collect an emergency contact name and phone number. Recommended for any event with physical activity, remote locations, or overnight stays.",
            },
            {
                field: "Waiver",
                description:
                    "Displays a waiver or terms text that attendees must accept before completing checkout. Write the full waiver text in the field that appears when this is enabled.",
                tips: [
                    "Have a legal professional review your waiver text.",
                    "Be specific about what risks or conditions the attendee is acknowledging.",
                ],
            },
            {
                field: "Custom Fields",
                description:
                    "Additional questions you want to ask at checkout. Supports text, textarea, number, date, dropdown, yes/no, and checkbox field types. Drag to reorder.",
                tips: [
                    "Group related fields using the Section selector (Custom, Personal Details, Registration Details, Race Details).",
                    "Only mark fields as Required if you truly need the answer — each required field increases checkout friction.",
                    "For dropdown fields, enter each option on a new line in the Options box.",
                ],
            },
        ],
    },

    coupons: {
        overview:
            "Create discount codes that attendees can apply at checkout. Coupons can offer a percentage off or a fixed amount off, with optional usage limits and validity windows.",
        fields: [
            {
                field: "Coupon Code",
                description:
                    "The code attendees enter at checkout (e.g. EARLY20, STAFF50). Codes are case-insensitive. Choose something easy to type and remember.",
            },
            {
                field: "Discount Type",
                description:
                    "Percentage discounts apply a proportion off the ticket price (e.g. 20% off). Fixed discounts deduct a set amount (e.g. N$50 off). Percentage discounts scale with ticket price; fixed discounts are constant.",
            },
            {
                field: "Discount Value",
                description:
                    "The size of the discount — either the percentage (0–100) or the fixed amount in the event's currency.",
            },
            {
                field: "Max Total Uses",
                description:
                    "How many times this code can be used in total across all customers. Leave blank for unlimited. Use this to create scarcity (e.g. first 50 customers only).",
            },
            {
                field: "Max Uses Per Customer",
                description:
                    "How many times a single customer can use this code. Leave blank for unlimited. Set to 1 to prevent a customer from stacking discounts across multiple orders.",
            },
            {
                field: "Validity Window",
                description:
                    "Optionally restrict when this coupon can be used. Useful for time-limited promotions (e.g. a 24-hour flash sale code).",
            },
            {
                field: "Minimum Order Amount",
                description:
                    "A minimum total order value required before the coupon applies. Leave blank for no minimum.",
            },
            {
                field: "Applicable Tickets",
                description:
                    "By default, coupons apply to all ticket types. Enter specific ticket IDs to restrict the discount to certain ticket types only.",
            },
            {
                field: "Enabled",
                description:
                    "Toggle a coupon off to temporarily deactivate it without deleting it. Disabled coupons cannot be used at checkout.",
            },
        ],
    },

    promoters: {
        overview:
            "Enlist existing NamEvents users to promote this event. Each promoter gets a share code and earns a commission on sales attributed to that code. Optionally, using a promoter's code can also give the buyer a discount.",
        fields: [
            {
                field: "Promoter Email",
                description:
                    "The email of the existing NamEvents user you want to enlist. They must already have an account. This cannot be changed once the promoter is added.",
            },
            {
                field: "Share Code",
                description:
                    "The code the promoter shares so their sales can be attributed. Leave blank to auto-generate one. Codes are case-insensitive and must be unique per event.",
            },
            {
                field: "Commission Type",
                description:
                    "Percentage commission pays a proportion of the (discounted) ticket subtotal on each attributed order. Fixed commission pays a flat amount per ticket sold.",
            },
            {
                field: "Commission Value",
                description:
                    "The size of the commission — either the percentage (0–100) or the fixed amount per ticket in the event's currency.",
            },
            {
                field: "Buyer Discount",
                description:
                    "Optionally attach a discount that applies when a buyer uses this promoter's code. The discount code is the promoter's share code, so there is no separate code to manage.",
                tips: [
                    "Leave the discount off to run attribution-only promotion where the buyer price is unchanged.",
                ],
            },
            {
                field: "Active",
                description:
                    "Deactivate a promoter to stop them earning commission and disable their code without losing their history.",
                tips: [
                    "Remove takes a promoter off the list entirely. Any sales they already made stay in the orders report.",
                ],
            },
        ],
    },

    "refund-policy": {
        overview:
            "Define your event's refund policy. This text is shown to attendees before checkout and in their order confirmation. A clear policy builds trust and reduces disputes.",
        fields: [
            {
                field: "Allow Refunds",
                description:
                    "Toggle on to enable refunds for this event. When off, all sales are final and the refund fields below are hidden.",
            },
            {
                field: "Refund Rate",
                description:
                    "The percentage of the ticket price that will be refunded (0–100%). A rate of 100% means a full refund; 50% means a partial refund of half the ticket price.",
                tips: [
                    "Consider offering 100% refunds up to a deadline and 50% after that to recover some costs.",
                ],
            },
            {
                field: "Refund Deadline",
                description:
                    "The number of days before the event start date by which refund requests must be submitted. Requests after this deadline are declined automatically.",
                tips: [
                    "Set this to at least 7 days before the event so you have time to manage refunds before finalising numbers with vendors.",
                ],
            },
            {
                field: "Policy Description",
                description:
                    "A plain-language description of your refund policy shown to attendees. Be explicit about the deadline, refund rate, and how refunds are processed (to original payment method, credit, etc.).",
            },
        ],
    },

    mailing: {
        overview:
            "Customise the automated emails sent to attendees after they purchase a ticket. You can tailor both the standard confirmation email and the complementary ticket email.",
        fields: [
            {
                field: "Confirmation Email",
                description:
                    "Sent automatically to every attendee immediately after a successful purchase. The message appears alongside the standard order details (event name, date, ticket type, QR code).",
                tips: [
                    "Use this space for a warm welcome, practical event-day instructions, or a call to action (e.g. 'Add the event to your calendar').",
                    "Keep it concise — attendees are skimming for their ticket details.",
                ],
            },
            {
                field: "Complementary Ticket Email",
                description:
                    "Sent when a ticket is issued manually as a complimentary (free) ticket. Useful for staff, speakers, or sponsors who receive tickets without purchasing.",
                tips: [
                    "Mention who the ticket is from and any VIP access it includes.",
                ],
            },
            {
                field: "Invoice Email",
                description:
                    "Sent when an order is placed with invoice payment. Only the initial invoice email uses this block. The invoice reminders and the expiry notice are not affected.",
                tips: [
                    "Write it as a thank you for the order rather than a payment instruction.",
                    "Leave out banking and account details, since those already appear on the invoice itself.",
                ],
            },
        ],
    },

    reminders: {
        overview:
            "Set up an automated reminder email sent to registered attendees before the event. This reduces no-shows and gives attendees a timely nudge with event details.",
        fields: [
            {
                field: "Enable Reminders",
                description:
                    "Toggle on to activate automated reminder emails for this event. The reminder is sent to all registered attendees at the specified time before the event.",
            },
            {
                field: "Hours Before",
                description:
                    "How far in advance of the event start time to send the reminder. Options are 12, 24, 48, or 72 hours before.",
                tips: [
                    "24 hours is the most common choice — enough notice for attendees to prepare without being too far in advance.",
                    "For events requiring travel, consider 48 or 72 hours.",
                ],
            },
        ],
    },

    sponsors: {
        overview:
            "Add sponsors to your event. Sponsor logos and names are displayed on the event listing page and can appear in event materials. Drag to reorder how sponsors appear.",
        fields: [
            {
                field: "Sponsor Name",
                description:
                    "The official name of the sponsoring organisation, displayed publicly on the event page.",
            },
            {
                field: "Logo",
                description:
                    "The sponsor's logo image. Displayed alongside the sponsor name on the event listing. Upload a clear, high-resolution logo with a transparent or white background.",
                tips: [
                    "PNG format with a transparent background works best across different page backgrounds.",
                    "Minimum recommended size: 200 × 200 px.",
                ],
            },
            {
                field: "Website URL",
                description:
                    "Optional link to the sponsor's website. The logo becomes a clickable link when this is provided.",
            },
            {
                field: "Tier",
                description:
                    "The sponsorship level: Title, Gold, Silver, Bronze, or Partner. This determines display prominence — Title sponsors appear most prominently.",
            },
            {
                field: "Active",
                description:
                    "Toggle off to temporarily hide a sponsor from the public listing without deleting them. Useful when a sponsor's details are being updated.",
            },
        ],
    },

    speakers: {
        overview:
            "Add the people appearing at your event and the programme they appear in. The Speakers and Line-up sections on the event page stay hidden until they have entries.",
        fields: [
            {
                field: "Speaker",
                description:
                    "Name, position, organisation, a one-line tagline and a longer bio in Markdown. Visitors open the bio from the speaker's card.",
                tips: [
                    "Photos are cropped to a circle. Keep the face centred.",
                    "Use the arrows or drag a row to set the order speakers appear in.",
                ],
            },
            {
                field: "Roles",
                description:
                    "One or more of Speaker, Keynote speaker, Presenter, Panellist, Panel moderator, Moderator, Host, Facilitator or Performer. The card shows them joined, for example \"Presenter & Panel moderator\". The first role is the default in line-up slots.",
            },
            {
                field: "Line-up slot",
                description:
                    "A time on an event day with a title, kind, stage and description, and the speakers taking part, each with their role in that slot. Times are local Windhoek time.",
                tips: [
                    "Slots that overlap on the same stage are flagged with a warning. You can still save them.",
                    "Leave the stage empty for something everyone attends, such as lunch.",
                ],
            },
        ],
    },

    feedback: {
        overview:
            "Collect feedback from attendees after the event ends. You can send a post-event email survey, capture responses at exit scan, or both. Custom questions let you gather the specific insights you need.",
        fields: [
            {
                field: "Enable Feedback",
                description:
                    "Toggle on to activate post-event feedback collection for this event.",
            },
            {
                field: "Collection Mode",
                description:
                    "How feedback is collected. 'Email after event' sends a survey link by email a set number of days after the event ends. 'Exit scan' displays a feedback prompt when the attendee's QR code is scanned at exit. 'Both' uses both methods.",
                tips: [
                    "Email gives attendees more time to reflect; exit scan captures immediate impressions.",
                ],
            },
            {
                field: "Email Delay",
                description:
                    "When using email collection, how many days after the event ends to send the feedback request. 1–3 days is typical — early enough that the experience is fresh.",
            },
            {
                field: "Feedback Questions",
                description:
                    "The specific questions in your feedback survey. Supports text, rating scale (1–10), yes/no, single choice, multiple choice, and grid formats. Drag to reorder.",
                tips: [
                    "Keep surveys short — aim for 5 questions or fewer to maximise completion rates.",
                    "Include at least one open-ended text question to capture unexpected insights.",
                    "For rating questions, specify what the scale represents (e.g. '1 = Very dissatisfied, 10 = Very satisfied').",
                ],
            },
        ],
    },

    options: {
        overview:
            "Fine-tune how this event behaves at the door, in payments, and across the platform. Toggles here are operational settings — they don't affect what attendees see on the public listing.",
        fields: [
            {
                field: "Cashless Enabled",
                description:
                    "Enable on-site cashless payments for this event. When on, attendees can top up a cashless account and pay vendors with their wristband or QR code.",
            },
            {
                field: "Staff Needed for Scanning",
                description:
                    "Flags that this event requires staff at entry to scan tickets. Used by the operations team to plan staffing for the event day.",
            },
            {
                field: "Staff Needed for Door Sales",
                description:
                    "Flags that this event requires staff at the door to sell tickets on the day. Used by the operations team to plan staffing.",
            },
            {
                field: "Featured",
                description:
                    "Promotes this event to the featured slot on the homepage and category pages. Only platform administrators can change this.",
            },
        ],
    },

    review: {
        overview:
            "Review all your event details before publishing. Once you publish, the event becomes visible to the public and attendees can start registering. You can still edit most details after publishing.",
        fields: [
            {
                field: "Event Details",
                description:
                    "Confirm the event name, dates, location, and description are accurate. These are the details attendees rely on to plan their attendance.",
            },
            {
                field: "Images",
                description:
                    "Ensure you've uploaded both a listing image and a cover image. Events without images perform significantly worse in search results and on the homepage.",
            },
            {
                field: "Tickets",
                description:
                    "Verify that at least one ticket type exists, pricing is correct, and capacity limits are set appropriately.",
            },
            {
                field: "Publishing",
                description:
                    "Clicking Publish makes your event live immediately. A draft event is only visible to you. You can unpublish an event at any time to take it offline.",
                tips: [
                    "Test the public listing view before sharing the link widely.",
                    "Notify your mailing list and social media channels as soon as you publish.",
                ],
            },
        ],
    },
};
